/**
 * counterBiasWhisper — surfaces opposite-side setups during cognitive
 * anchoring.
 *
 * Pain point this addresses (user, 2026-05-18): woke up bullish, was
 * watching for a long off the 15M 21EMA bounce, missed a textbook clean
 * Golden Crayon rejection short on the 4H/15M. The bias was actively
 * filtering out the real setup.
 *
 * Detection:
 *   1. Extract the trader's stated session bias from the journal
 *      thesis (reuse extractIntent from thesisValidation).
 *   2. Run all 7 Wave C pattern detectors WITH THE OPPOSITE DIRECTION
 *      against the most recent closed 5m bar.
 *   3. Filter the results for "actionable" patterns (those a discretionary
 *      trader would actually trade if they saw them — not low-quality
 *      signal bars or weak Wolf Stacks).
 *   4. If any actionable opposite-direction pattern fires AND the trader
 *      hasn't already taken a trade in that direction recently, surface
 *      it as a Counter-Bias Whisper.
 *
 * Framing principle (from conversation): not judgment, not "you missed
 * this." Just awareness. The whisper says "Bar action fired X opposite
 * your stated bias. You don't have a position. Worth a fresh look?"
 *
 * Pure function. No side effects. The polling + dismiss logic lives in
 * the useCounterBiasWhisper hook.
 */

import { extractIntent } from './analyzers/thesisValidation.js';
import { detectSetupPatterns, PATTERN_CATALOG } from '../barAnalysis/setupPatterns.js';

// Patterns the whisper considers actionable opposite-direction triggers.
// Tuned against the Wave C detectors:
//   - Wolf Stack: only when ≥ 3/4 alignment (Diaz's threshold)
//   - Signal Bar Quality: only at the strongest score (3/3)
//   - Holy Grail, 3-Bar Play, Uno Reverse, H2, L2: binary fires
//   - Turtle Soup: only when the reversal direction matches the
//     proposed opposite (alignsWithTrade=true here, since we're
//     running the detector with `direction = opposite`)
const isActionablePattern = (p) => {
  if (!p || !p.type) return false;
  switch (p.type) {
    case 'wolf-stack':         return p.score >= 3;
    case 'signal-bar-quality': return p.score === 3;
    case 'holy-grail':
    case '3-bar-play':
    case 'uno-reverse':
    case 'h2':
    case 'l2':
      return true;
    case 'turtle-soup':        return p.alignsWithTrade === true;
    default: return false;
  }
};

const normalizeDirection = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();
  if (s === 'long' || s === 'buy' || s === 'l') return 'long';
  if (s === 'short' || s === 'sell' || s === 's') return 'short';
  return null;
};

/**
 * Detect a counter-bias setup. Pure function — caller handles polling,
 * dedup, dismiss state.
 *
 * @param {Object} params
 * @param {string} params.thesis        Journal entry thesis text. Required to extract bias.
 * @param {Array}  params.bars5m        5m bars. Most recent must be a CLOSED bar
 *                                      (don't pass in-progress bar — it would change shape mid-bar).
 * @param {string} params.symbol        Symbol the bars belong to (for whisper key + display).
 * @param {string} [params.recentTradeDirection]  Direction of the trader's most recent
 *                                      trade today. If it matches the opposite direction
 *                                      (i.e., user already noticed and entered), skip the whisper.
 * @param {Object} [params.options]
 * @param {number} [params.options.minBars=30]   Minimum bar count to run detectors.
 * @returns {Object|null}  Whisper object or null when no actionable counter-bias setup.
 *
 * Whisper shape:
 *   {
 *     key:               stable dedup key
 *     type:              'counter-bias'
 *     bias:              'long' | 'short' — the trader's stated bias
 *     oppositeDirection: 'long' | 'short' — the direction the pattern fired
 *     primaryPattern:    pattern type string
 *     primaryLabel:      human-readable label
 *     patternsFired:     array of all actionable pattern types that fired
 *     barTime:           Unix sec of the bar that triggered
 *     barPrice:          close of the trigger bar
 *     symbol:            instrument symbol
 *     title:             short headline for the card
 *     detail:            descriptive body text
 *     action:            single-line suggestion
 *   }
 */
export const detectCounterBiasSetup = ({
  thesis,
  bars5m,
  symbol,
  recentTradeDirection,
  options = {},
} = {}) => {
  if (!thesis || typeof thesis !== 'string') return null;
  if (!Array.isArray(bars5m) || bars5m.length < (options.minBars ?? 30)) return null;
  if (!symbol || typeof symbol !== 'string') return null;

  // Extract bias. extractIntent returns 'long' | 'short' | 'either' | null.
  // We need a clear directional bias — skip when ambiguous.
  const bias = extractIntent(thesis);
  if (!bias || bias === 'either') return null;

  const oppositeDir = bias === 'long' ? 'short' : 'long';

  // If the trader's most recent trade today was IN the opposite direction,
  // they've clearly already noticed the setup and acted on it. Skip the
  // whisper — they don't need Coach pointing it out.
  const recentDir = normalizeDirection(recentTradeDirection);
  if (recentDir === oppositeDir) return null;

  // Use the most recent CLOSED bar as the trigger candidate.
  const triggerBar = bars5m[bars5m.length - 1];
  if (!triggerBar) return null;
  const barTime = Number(triggerBar.time);
  const barPrice = Number(triggerBar.close);
  if (!Number.isFinite(barTime) || !Number.isFinite(barPrice)) return null;

  // Run all 7 detectors with direction = opposite of bias.
  const patterns = detectSetupPatterns({
    bars: bars5m,
    entryTs: barTime,
    entryPrice: barPrice,
    direction: oppositeDir,
  });

  const actionable = patterns.filter(isActionablePattern);
  if (actionable.length === 0) return null;

  // Choose a primary pattern for the headline. Order of preference:
  // 1. 3-Bar Play (most specific, Rake's signature)
  // 2. Holy Grail (high-confidence pullback)
  // 3. Uno Reverse (level-based, Rake's framework)
  // 4. H2/L2 (Brooks structural)
  // 5. Wolf Stack (alignment)
  // 6. Turtle Soup (counter-trend)
  // 7. Signal Bar Quality (bar shape only)
  const PREFERENCE = ['3-bar-play', 'holy-grail', 'uno-reverse', 'h2', 'l2', 'wolf-stack', 'turtle-soup', 'signal-bar-quality'];
  const primary = PREFERENCE
    .map(type => actionable.find(p => p.type === type))
    .filter(Boolean)[0] || actionable[0];

  const primaryLabel = PATTERN_CATALOG[primary.type]?.label || primary.type;
  const otherCount = actionable.length - 1;
  const oppositeUpper = oppositeDir.toUpperCase();
  const biasUpper = bias.toUpperCase();

  const additionalSuffix = otherCount > 0
    ? ` + ${otherCount} more pattern${otherCount === 1 ? '' : 's'}`
    : '';

  return {
    // Dedup key: same pattern on the same bar shouldn't re-fire. Symbol
    // included so multi-instrument polling doesn't collide.
    key: `counter-bias:${symbol}:${barTime}:${primary.type}`,
    type: 'counter-bias',
    bias,
    oppositeDirection: oppositeDir,
    primaryPattern: primary.type,
    primaryLabel,
    patternsFired: actionable.map(p => p.type),
    barTime,
    barPrice,
    symbol,
    title: `${primaryLabel} just fired ${oppositeUpper} against your ${biasUpper} bias`,
    detail: `5m bar at ${barPrice} triggered ${primaryLabel}${additionalSuffix} in the OPPOSITE direction to your stated ${bias} bias. You don't have a position in this direction. Worth a fresh look at the chart?`,
    action: 'Pre-session bias filters cognition. Re-examine the chart as if you had no bias — the textbook setup may be the one your plan ignored.',
  };
};

// Exports for tests
export { isActionablePattern };
